import { useCallback, useRef, useState } from "react";

import { addMyFilesToRequest } from "@/features/my-files/api/addMyFilesToRequest";
import { useMultipartUploadMutations } from "@/features/my-files/components/upload-files-modal/hooks/useMultipartUploadMutations";
import {
  getExtensionFromFilename,
  parseFileName,
} from "@/utils/getExtensionFromMime.ts";

import type { FileVersionMetadata } from "../types/fileVersionMetadata";

import { findPreviousVersionId } from "../utils/sessionFileNaming";
import { uploadMyFileMultipart } from "../utils/uploadMyFileMultipart";
import { useConfidenceCheckerMyFiles } from "./useConfidenceCheckerMyFiles";

export type SourceFileStatus = "attached" | "error" | "uploaded" | "uploading";

export interface SourceFileRow {
  attachmentId?: number;
  clientId: string;
  displayBaseName: string;
  error?: string;
  file: File;
  previousVersionId: null | number;
  progress?: number;
  status: SourceFileStatus;
}

interface UseSourceFileUploadsReturn {
  addFiles: (files: File[]) => void;
  attachToRequest: () => Promise<void>;
  isUploading: boolean;
  removeFile: (clientId: string) => void;
  reset: () => void;
  sourceFiles: SourceFileRow[];
  updateFileMetadata: (clientId: string, meta: FileVersionMetadata) => void;
}

function toBaseName(fileName: string): string {
  try {
    return parseFileName(fileName).baseName;
  } catch {
    return fileName;
  }
}

export function useSourceFileUploads(
  requestId: number,
): UseSourceFileUploadsReturn {
  const multipartMutations = useMultipartUploadMutations();
  const { data: requestMyFiles } = useConfidenceCheckerMyFiles(requestId);
  const [sourceFiles, setSourceFiles] = useState<SourceFileRow[]>([]);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const sourceFilesRef = useRef<SourceFileRow[]>([]);

  const patch = useCallback(
    (clientId: string, next: Partial<SourceFileRow>) => {
      setSourceFiles((prev) => {
        const updated = prev.map((row) =>
          row.clientId === clientId ? { ...row, ...next } : row,
        );

        sourceFilesRef.current = updated;

        return updated;
      });
    },
    [],
  );

  const uploadOne = useCallback(
    async (row: SourceFileRow) => {
      const abortController = new AbortController();

      abortControllersRef.current.set(row.clientId, abortController);

      try {
        const attachmentId = await uploadMyFileMultipart({
          abortController,
          file: row.file,
          mutations: multipartMutations,
          onProgress: (progress) => patch(row.clientId, { progress }),
          requestId,
        });

        abortControllersRef.current.delete(row.clientId);
        patch(row.clientId, {
          attachmentId,
          progress: 100,
          status: "uploaded",
        });
      } catch (error) {
        abortControllersRef.current.delete(row.clientId);

        if (abortController.signal.aborted) return;

        patch(row.clientId, {
          error: error instanceof Error ? error.message : "Upload failed",
          status: "error",
        });
      }
    },
    [multipartMutations, patch, requestId],
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const rows: SourceFileRow[] = files.map((file) => ({
        clientId: crypto.randomUUID(),
        displayBaseName: toBaseName(file.name),
        file,
        previousVersionId: findPreviousVersionId(file.name, requestMyFiles),
        progress: 0,
        status: "uploading",
      }));

      setSourceFiles((prev) => {
        const updated = [...prev, ...rows];

        sourceFilesRef.current = updated;

        return updated;
      });

      for (const row of rows) {
        void uploadOne(row);
      }
    },
    [requestMyFiles, uploadOne],
  );

  const removeFile = useCallback((clientId: string) => {
    const controller = abortControllersRef.current.get(clientId);

    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(clientId);
    }

    setSourceFiles((prev) => {
      const updated = prev.filter((row) => row.clientId !== clientId);

      sourceFilesRef.current = updated;

      return updated;
    });
  }, []);

  const updateFileMetadata = useCallback(
    (clientId: string, meta: FileVersionMetadata) => {
      patch(clientId, meta);
    },
    [patch],
  );

  const reset = useCallback(() => {
    for (const controller of abortControllersRef.current.values()) {
      controller.abort();
    }
    abortControllersRef.current.clear();
    sourceFilesRef.current = [];
    setSourceFiles([]);
  }, []);

  const attachToRequest = useCallback(async () => {
    const uploaded = sourceFilesRef.current.filter(
      (row): row is { attachmentId: number } & SourceFileRow =>
        row.status === "uploaded" && row.attachmentId !== undefined,
    );

    if (uploaded.length === 0) return;

    await addMyFilesToRequest(requestId, {
      myFiles: uploaded.map((row) => {
        const extension = getExtensionFromFilename(row.file.name);

        return {
          attachmentId: row.attachmentId,
          fileName: row.file.name,
          isSourceFile: true,
          name:
            extension === "unknown"
              ? row.displayBaseName
              : `${row.displayBaseName}.${extension}`,
        };
      }),
      postOnActivityLog: false,
    });

    for (const row of uploaded) {
      patch(row.clientId, { status: "attached" });
    }
  }, [patch, requestId]);

  const isUploading = sourceFiles.some((row) => row.status === "uploading");

  return {
    addFiles,
    attachToRequest,
    isUploading,
    removeFile,
    reset,
    sourceFiles,
    updateFileMetadata,
  };
}
